import { useMemo, useRef, useState } from "react";

import { TrashIcon } from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";

// Annotations are persisted as a JSON string inside the check-in item's
// `value` field, which keeps the storage path identical to every other step
// (server or localStorage fallback). Keeping the shape flat and primitive
// makes it trivial for the backend to surface later — no new model needed.
type Severity = "minor" | "moderate" | "severe";

export interface DamageMark {
  id: string;
  /** X coordinate as a percentage of the diagram width (0–100). */
  x: number;
  /** Y coordinate as a percentage of the diagram height (0–100). */
  y: number;
  severity: Severity;
  note?: string;
}

interface SeverityMeta {
  id: Severity;
  label: string;
  dotClass: string;
  ringClass: string;
  fill: string;
  stroke: string;
  textClass: string;
  badgeClass: string;
}

const SEVERITIES: SeverityMeta[] = [
  {
    id: "minor",
    label: "Minor",
    dotClass: "bg-amber-400",
    ringClass: "ring-amber-500",
    fill: "#fbbf24",
    stroke: "#b45309",
    textClass: "text-amber-700",
    badgeClass: "bg-amber-100 text-amber-800 ring-amber-200",
  },
  {
    id: "moderate",
    label: "Moderate",
    dotClass: "bg-orange-500",
    ringClass: "ring-orange-500",
    fill: "#f97316",
    stroke: "#c2410c",
    textClass: "text-orange-700",
    badgeClass: "bg-orange-100 text-orange-800 ring-orange-200",
  },
  {
    id: "severe",
    label: "Severe",
    dotClass: "bg-rose-600",
    ringClass: "ring-rose-600",
    fill: "#e11d48",
    stroke: "#9f1239",
    textClass: "text-rose-700",
    badgeClass: "bg-rose-100 text-rose-800 ring-rose-200",
  },
];

const severityMeta = (id: Severity) =>
  SEVERITIES.find((s) => s.id === id) ?? SEVERITIES[0];

const clamp = (n: number, min = 0, max = 100) =>
  Math.max(min, Math.min(max, n));

const parseMarks = (value: string): DamageMark[] => {
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(
        (m): m is DamageMark =>
          m &&
          typeof m === "object" &&
          typeof m.id === "string" &&
          typeof m.x === "number" &&
          typeof m.y === "number" &&
          (m.severity === "minor" ||
            m.severity === "moderate" ||
            m.severity === "severe")
      )
      .map((m) => ({
        id: m.id,
        x: clamp(m.x),
        y: clamp(m.y),
        severity: m.severity,
        note: typeof m.note === "string" ? m.note : undefined,
      }));
  } catch {
    return [];
  }
};

const newId = () =>
  `m-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

interface DamageDiagramEditorProps {
  value: string;
  onChange: (nextValue: string, hasMarks: boolean) => void;
  disabled?: boolean;
}

/**
 * Interactive top-down vehicle diagram for marking damage locations during
 * check-in. Each click adds a coloured pin at the clicked coordinates using
 * the currently-selected severity. Pins can be re-classified, annotated with
 * a short note, or removed individually; a "Clear all" action resets the
 * diagram entirely.
 *
 * The editor is a controlled component — the diagram state is kept in the
 * parent's value (a JSON string) so it threads through the same persistence
 * path as every other check-in answer.
 */
export default function DamageDiagramEditor({
  value,
  onChange,
  disabled,
}: DamageDiagramEditorProps) {
  const [severity, setSeverity] = useState<Severity>("minor");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);

  const marks = useMemo(() => parseMarks(value), [value]);

  const commit = (next: DamageMark[], nextSelected: string | null = selectedId) => {
    onChange(JSON.stringify(next), next.length > 0);
    if (nextSelected !== selectedId) setSelectedId(nextSelected);
  };

  const addMarkAt = (clientX: number, clientY: number) => {
    if (disabled) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    // The diagram's visual body is inset from the SVG edges by the tyres.
    // We still let operators pin anywhere inside the SVG so bumper / wheel /
    // mirror damage can be recorded — anything outside the body outline is
    // valid too.
    const x = clamp(((clientX - rect.left) / rect.width) * 100);
    const y = clamp(((clientY - rect.top) / rect.height) * 100);
    const mark: DamageMark = {
      id: newId(),
      x,
      y,
      severity,
    };
    commit([...marks, mark], mark.id);
  };

  const updateMark = (id: string, patch: Partial<DamageMark>) => {
    commit(marks.map((m) => (m.id === id ? { ...m, ...patch } : m)));
  };

  const removeMark = (id: string) => {
    commit(
      marks.filter((m) => m.id !== id),
      null
    );
  };

  const clearAll = () => {
    commit([], null);
  };

  const selected = selectedId ? marks.find((m) => m.id === selectedId) : null;

  return (
    <div className="space-y-3">
      {/* Severity picker + summary toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Severity
          </span>
          {SEVERITIES.map((s) => {
            const active = s.id === severity;
            return (
              <button
                key={s.id}
                type="button"
                disabled={disabled}
                onClick={() => setSeverity(s.id)}
                className={classNames(
                  "inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ring-1 transition-all",
                  active
                    ? "bg-white text-slate-800 ring-slate-400 shadow-sm"
                    : "bg-white/60 text-slate-500 ring-slate-200 hover:bg-white",
                  disabled && "cursor-not-allowed opacity-60"
                )}
                aria-pressed={active}
              >
                <span
                  className={classNames(
                    "h-2.5 w-2.5 rounded-full ring-2 ring-white",
                    s.dotClass
                  )}
                />
                {s.label}
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">
            {marks.length === 0
              ? "Click the diagram to mark damage"
              : `${marks.length} mark${marks.length === 1 ? "" : "s"}`}
          </span>
          {marks.length > 0 && !disabled && (
            <button
              type="button"
              onClick={clearAll}
              className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50"
            >
              <TrashIcon className="h-3.5 w-3.5" aria-hidden="true" />
              Clear all
            </button>
          )}
        </div>
      </div>

      {/* Diagram */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,14rem)]">
        <div
          className={classNames(
            "relative overflow-hidden rounded-xl border border-slate-200 bg-slate-50",
            disabled && "pointer-events-none opacity-70"
          )}
        >
          <svg
            ref={svgRef}
            viewBox="0 0 400 800"
            preserveAspectRatio="xMidYMid meet"
            className="block h-auto w-full cursor-crosshair select-none"
            onClick={(e) => addMarkAt(e.clientX, e.clientY)}
            role="img"
            aria-label="Top-down vehicle damage diagram. Click to mark damage."
          >
            {/* Orientation labels */}
            <text
              x="200"
              y="28"
              textAnchor="middle"
              className="fill-slate-400"
              style={{ fontSize: 14, fontWeight: 600, letterSpacing: 2 }}
            >
              FRONT
            </text>
            <text
              x="200"
              y="788"
              textAnchor="middle"
              className="fill-slate-400"
              style={{ fontSize: 14, fontWeight: 600, letterSpacing: 2 }}
            >
              REAR
            </text>

            {/* Tyres */}
            <g fill="#1f2937">
              <rect x="52" y="170" width="34" height="80" rx="8" />
              <rect x="314" y="170" width="34" height="80" rx="8" />
              <rect x="52" y="550" width="34" height="80" rx="8" />
              <rect x="314" y="550" width="34" height="80" rx="8" />
            </g>

            {/* Body outline */}
            <rect
              x="90"
              y="60"
              width="220"
              height="680"
              rx="52"
              ry="52"
              fill="#e2e8f0"
              stroke="#94a3b8"
              strokeWidth="2"
            />

            {/* Windshield */}
            <path
              d="M 108 220 Q 200 196 292 220 L 282 304 Q 200 290 118 304 Z"
              fill="#cbd5e1"
              stroke="#94a3b8"
              strokeWidth="1.25"
            />
            {/* Rear window */}
            <path
              d="M 118 500 Q 200 514 282 500 L 292 580 Q 200 604 108 580 Z"
              fill="#cbd5e1"
              stroke="#94a3b8"
              strokeWidth="1.25"
            />
            {/* Roof panel (between glass) */}
            <rect
              x="112"
              y="304"
              width="176"
              height="196"
              fill="#d6deea"
              stroke="#94a3b8"
              strokeWidth="1"
            />

            {/* Wing mirrors */}
            <g fill="#94a3b8">
              <rect x="82" y="346" width="14" height="24" rx="4" />
              <rect x="304" y="346" width="14" height="24" rx="4" />
            </g>

            {/* Panel dividers (dashed) */}
            <g
              stroke="#94a3b8"
              strokeWidth="1"
              strokeDasharray="4 4"
              fill="none"
            >
              {/* Front bumper / bonnet */}
              <line x1="90" y1="140" x2="310" y2="140" />
              {/* Bonnet / windshield */}
              <line x1="108" y1="220" x2="292" y2="220" />
              {/* Rear window / boot */}
              <line x1="108" y1="580" x2="292" y2="580" />
              {/* Boot / rear bumper */}
              <line x1="90" y1="660" x2="310" y2="660" />
              {/* Door split, driver side */}
              <line x1="112" y1="402" x2="288" y2="402" />
            </g>

            {/* Headlights */}
            <g fill="#fde68a" stroke="#ca8a04" strokeWidth="1">
              <ellipse cx="130" cy="96" rx="16" ry="8" />
              <ellipse cx="270" cy="96" rx="16" ry="8" />
            </g>
            {/* Tail lights */}
            <g fill="#fecaca" stroke="#b91c1c" strokeWidth="1">
              <rect x="112" y="692" width="40" height="14" rx="3" />
              <rect x="248" y="692" width="40" height="14" rx="3" />
            </g>

            {/* Panel labels — subtle guidance for operators */}
            <g
              className="fill-slate-400"
              textAnchor="middle"
              style={{ fontSize: 10, letterSpacing: 1 }}
            >
              <text x="200" y="182">BONNET</text>
              <text x="200" y="406">ROOF</text>
              <text x="200" y="628">BOOT</text>
            </g>

            {/* Damage marks */}
            {marks.map((m) => {
              const meta = severityMeta(m.severity);
              const isSelected = m.id === selectedId;
              return (
                <g
                  key={m.id}
                  transform={`translate(${(m.x / 100) * 400} ${(m.y / 100) * 800})`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedId(m.id);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  {isSelected && (
                    <circle
                      r="14"
                      fill="none"
                      stroke={meta.stroke}
                      strokeWidth="1.5"
                      strokeDasharray="3 3"
                    />
                  )}
                  <circle
                    r="9"
                    fill={meta.fill}
                    stroke={meta.stroke}
                    strokeWidth="1.5"
                  />
                  <circle r="3" fill="#ffffff" />
                </g>
              );
            })}
          </svg>
        </div>

        {/* Marks list / editor */}
        <div className="flex flex-col gap-2 rounded-xl border border-slate-200 bg-white p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Damage marks
          </p>
          {marks.length === 0 ? (
            <p className="text-xs text-slate-500">
              No damage recorded yet. Pick a severity above and click anywhere
              on the diagram to drop a pin.
            </p>
          ) : (
            <ul className="flex max-h-72 flex-col gap-2 overflow-y-auto pr-1">
              {marks.map((m, idx) => {
                const meta = severityMeta(m.severity);
                const isSelected = m.id === selectedId;
                return (
                  <li
                    key={m.id}
                    className={classNames(
                      "rounded-lg border px-2.5 py-2 text-xs transition-all",
                      isSelected
                        ? "border-slate-400 bg-slate-50 ring-1 ring-slate-300"
                        : "border-slate-200 hover:border-slate-300"
                    )}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <button
                        type="button"
                        onClick={() => setSelectedId(m.id)}
                        className="flex min-w-0 items-center gap-2 text-left"
                      >
                        <span
                          className={classNames(
                            "h-2.5 w-2.5 flex-none rounded-full",
                            meta.dotClass
                          )}
                        />
                        <span className="truncate font-semibold text-slate-700">
                          Mark {idx + 1}
                        </span>
                        <span
                          className={classNames(
                            "inline-flex flex-none items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1",
                            meta.badgeClass
                          )}
                        >
                          {meta.label}
                        </span>
                      </button>
                      {!disabled && (
                        <button
                          type="button"
                          onClick={() => removeMark(m.id)}
                          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-rose-600"
                          aria-label={`Remove mark ${idx + 1}`}
                        >
                          <TrashIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        </button>
                      )}
                    </div>

                    {isSelected && !disabled && (
                      <div className="mt-2 space-y-2">
                        <div className="flex items-center gap-1">
                          {SEVERITIES.map((s) => {
                            const active = s.id === m.severity;
                            return (
                              <button
                                key={s.id}
                                type="button"
                                onClick={() =>
                                  updateMark(m.id, { severity: s.id })
                                }
                                className={classNames(
                                  "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-medium ring-1 transition-all",
                                  active
                                    ? "bg-white text-slate-800 ring-slate-400"
                                    : "bg-slate-50 text-slate-500 ring-slate-200 hover:bg-white"
                                )}
                              >
                                <span
                                  className={classNames(
                                    "h-2 w-2 rounded-full",
                                    s.dotClass
                                  )}
                                />
                                {s.label}
                              </button>
                            );
                          })}
                        </div>
                        <input
                          type="text"
                          value={m.note ?? ""}
                          onChange={(e) =>
                            updateMark(m.id, { note: e.target.value })
                          }
                          placeholder="Optional note (e.g. dent, scratch)"
                          className="block w-full rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 placeholder:text-slate-400 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                        />
                      </div>
                    )}

                    {!isSelected && m.note && (
                      <p className="mt-1 truncate text-[11px] text-slate-500">
                        {m.note}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
